import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { LinkIcon } from '@heroicons/react/24/outline';
import { Button } from '../ui/Button';
import { healthConnectSupported, syncHealthConnect } from '../../utils/healthConnect';

/**
 * Health Connect device row for MePage's "Connected Devices" card.
 *
 * Android-native only (renders nothing on web/iOS). Health Connect is a
 * push-from-app source: there's no OAuth "connect" — tapping Sync requests the
 * on-device permission, reads steps + weight, and POSTs them to the backend.
 */
export default function HealthConnectRow() {
  const [syncing, setSyncing] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  if (!healthConnectSupported()) return null;

  const handleSync = async () => {
    setSyncing(true);
    try {
      const result = await syncHealthConnect({ days: 30 });
      setLastResult(result);
      if (result.note && result.written === 0) {
        toast(result.note, { icon: 'ℹ️' });
      } else {
        toast.success(`Synced ${result.written} record${result.written === 1 ? '' : 's'} from Health Connect`);
      }
    } catch (err) {
      toast.error(err?.message || 'Health Connect sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const connected = lastResult && lastResult.written > 0;

  return (
    <div className="flex items-center justify-between p-4 mt-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
      <div className="flex items-center space-x-3">
        <div className="flex-shrink-0">
          {connected ? (
            <CheckCircleIcon className="w-6 h-6 text-green-500" />
          ) : (
            <LinkIcon className="w-6 h-6 text-gray-400" />
          )}
        </div>
        <div>
          <p className="text-sm font-medium text-gray-900 dark:text-white">Health Connect</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {connected
              ? `Last sync: ${lastResult.written} record${lastResult.written === 1 ? '' : 's'}`
              : 'Samsung Health, Pixel & other on-device data'}
          </p>
        </div>
      </div>
      <div>
        <Button variant="outline" size="sm" onClick={handleSync} disabled={syncing}>
          {syncing ? 'Syncing…' : 'Sync'}
        </Button>
      </div>
    </div>
  );
}
